import type {VadoCatalog} from "#pricing/catalogs/vado/catalog"
import type {PriceGraphData} from "#pricing/catalogs/graph-helpers"
import {createPriceContainer, createVariantConditionedPriceNodes} from "#pricing/catalogs/graph-helpers"
import {ConditionalAmountGroupNode} from "#pricing/nodes/conditional-amount-group-node"
import {ConditionalAmountList} from "#pricing/nodes/conditional-amount-list"
import {ConditionalAmountNode} from "#pricing/nodes/conditional-amount-node"
import {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import type {PriceContainer} from "#pricing/nodes/price-container"
import type {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"

export function createSafariPriceGraph(catalog: VadoCatalog): PriceGraphData {
    const result: PriceGraphData = {
        amountGroups: [],
        pricedItemNodes: [],
        priceContainers: [],
    }

    //---------------------------------------------Configuration groups---------------------------------------------

    let VanityStyle = new ConfigurationGroupNode({
        parameters: {
            groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693",
            groupName: "",
            currentVariant: {
                groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693",
                groupName: "",
                variantId: "f2a6cc30-218f-4962-b2d1-2231433bae99",
                variantName: "",
            },
            allVariants: [
                {groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693", groupName: "", variantId: "fluted", variantName: ""},
                {groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693", groupName: "", variantId: "smooth", variantName: ""},
            ],
        },
    })

    let VanitySize = new ConfigurationGroupNode({
        parameters: {
            groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae",
            groupName: "",
            currentVariant: {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_left", variantName: ""},
            allVariants: [
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_left", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_right", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "500", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "600", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "800", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "1000", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "1200", variantName: ""},
            ],
        },
    })

    let VanityMount = new ConfigurationGroupNode({
        parameters: {
            groupId: "mount",
            groupName: "",
            currentVariant: {groupId: "mount", groupName: "", variantId: "wall_hung", variantName: ""},
            allVariants: [
                {groupId: "mount", groupName: "", variantId: "wall_hung", variantName: ""},
                {groupId: "mount", groupName: "", variantId: "floor_mounted", variantName: ""},
            ],
        },
    })

    let HandleType = new ConfigurationGroupNode({
        parameters: {
            groupId: "handle",
            groupName: "",
            currentVariant: {groupId: "handle", groupName: "", variantId: "knob", variantName: ""},
            allVariants: [
                {groupId: "handle", groupName: "", variantId: "knob", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "knob_fluted", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "knob_small", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "bar", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "top_mount", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "pull_handle", variantName: ""},
            ],
        },
    })

    let HandleFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "handle_comb_finish",
            groupName: "",
            currentVariant: {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_chrome", variantName: ""},
            allVariants: [
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_chrome", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_matt_black", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_satin_brass", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_pewter", variantName: ""},
            ],
        },
    })

    let BasinWaste = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_waste",
            groupName: "",
            currentVariant: {groupId: "basin_waste", groupName: "", variantId: "comb_with_waste", variantName: ""},
            allVariants: [
                {groupId: "basin_waste", groupName: "", variantId: "comb_with_waste", variantName: ""},
                {groupId: "basin_waste", groupName: "", variantId: "comb_without_waste", variantName: ""},
            ],
        },
    })

    let TapFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_comb_finish",
            groupName: "",
            currentVariant: {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_chrome", variantName: ""},
            allVariants: [
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_chrome", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_matt_black", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_satin_brass", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_pewter", variantName: ""},
            ],
        },
    })

    let LeverOption = new ConfigurationGroupNode({
        parameters: {
            groupId: "lever_option_handle",
            groupName: "",
            currentVariant: {groupId: "lever_option_handle", groupName: "", variantId: "lever_handle", variantName: ""},
            allVariants: [
                {groupId: "lever_option_handle", groupName: "", variantId: "lever_handle", variantName: ""},
                {groupId: "lever_option_handle", groupName: "", variantId: "leverless_handle", variantName: ""},
            ],
        },
    })

    let BottleTrap = new ConfigurationGroupNode({
        parameters: {
            groupId: "bottle_trap",
            groupName: "",
            currentVariant: {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_with", variantName: ""},
            allVariants: [
                {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_with", variantName: ""},
                {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_without", variantName: ""},
            ],
        },
    })

    let BottleTrapFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "bottle_trap_finish",
            groupName: "",
            currentVariant: {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_chrome", variantName: ""},
            allVariants: [
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_chrome", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_matt_black", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_satin_brass", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_pewter", variantName: ""},
            ],
        },
    })

    let VanityColourWithNaturalOak = new ConfigurationGroupNode({
        parameters: {
            groupId: "furniture_finish",
            groupName: "",
            currentVariant: {groupId: "furniture_finish", groupName: "", variantId: "arctic_white", variantName: ""},
            allVariants: [
                {groupId: "furniture_finish", groupName: "", variantId: "arctic_white", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "sage_green", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "charcoal_grey", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "atlantic_blue", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "terracotta", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "cashmere", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "natural_oak", variantName: ""},
            ],
        },
    })

    let BasinDesign400_500 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_400-500",
            groupName: "",
            currentVariant: {groupId: "basin_400-500", groupName: "", variantId: "mineral_400-500", variantName: ""},
            allVariants: [
                {groupId: "basin_400-500", groupName: "", variantId: "mineral_400-500", variantName: ""},
                {groupId: "basin_400-500", groupName: "", variantId: "ceramic_400-500", variantName: ""},
            ],
        },
    })

    let BasinOverflowFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow_finish",
            groupName: "",
            currentVariant: {groupId: "overflow_finish", groupName: "", variantId: "overflow_chrome", variantName: ""},
            allVariants: [
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_chrome", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_matt_black", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_satin_brass", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_pewter", variantName: ""},
            ],
        },
    })

    let BasinDesign600_800 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_600-800",
            groupName: "",
            currentVariant: {groupId: "basin_600-800", groupName: "", variantId: "mineral_600-800", variantName: ""},
            allVariants: [
                {groupId: "basin_600-800", groupName: "", variantId: "mineral_600-800", variantName: ""},
                {groupId: "basin_600-800", groupName: "", variantId: "ceramic_600-800", variantName: ""},
                {groupId: "basin_600-800", groupName: "", variantId: "countertop_600-800", variantName: ""},
            ],
        },
    })

    let TapMineralInset = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_mineral_inset",
            groupName: "",
            currentVariant: {groupId: "tap_mineral_inset", groupName: "", variantId: "100/200", variantName: ""},
            allVariants: [
                {groupId: "tap_mineral_inset", groupName: "", variantId: "100/200", variantName: ""},
                {groupId: "tap_mineral_inset", groupName: "", variantId: "101/201", variantName: ""},
                {groupId: "tap_mineral_inset", groupName: "", variantId: "102/202", variantName: ""},
            ],
        },
    })

    let BasinDesign1000_1200 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_1000-1200",
            groupName: "",
            currentVariant: {groupId: "basin_1000-1200", groupName: "", variantId: "mineral_1000-1200", variantName: ""},
            allVariants: [
                {groupId: "basin_1000-1200", groupName: "", variantId: "mineral_1000-1200", variantName: ""},
                {groupId: "basin_1000-1200", groupName: "", variantId: "countertop_1000-1200", variantName: ""},
            ],
        },
    })

    let BasinFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_finish_mineral",
            groupName: "",
            currentVariant: {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_arctic_white", variantName: ""},
            allVariants: [
                {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_arctic_white", variantName: ""},
                {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_gross_white", variantName: ""},
            ],
        },
    })

    let TapCounter = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_counter",
            groupName: "",
            currentVariant: {groupId: "tap_counter", groupName: "", variantId: "100e/200e", variantName: ""},
            allVariants: [
                {groupId: "tap_counter", groupName: "", variantId: "100e/200e", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109/209", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109s/209s", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109s-sq/209s-sq", variantName: ""},
            ],
        },
    })

    let BasinShape = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_shape",
            groupName: "",
            currentVariant: {groupId: "basin_shape", groupName: "", variantId: "round", variantName: ""},
            allVariants: [
                {groupId: "basin_shape", groupName: "", variantId: "round", variantName: ""},
                {groupId: "basin_shape", groupName: "", variantId: "round_fluted", variantName: ""},
                {groupId: "basin_shape", groupName: "", variantId: "square", variantName: ""},
            ],
        },
    })

    let WorktopFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "worktop_finish",
            groupName: "",
            currentVariant: {groupId: "worktop_finish", groupName: "", variantId: "wt_gross_white", variantName: ""},
            allVariants: [
                {groupId: "worktop_finish", groupName: "", variantId: "wt_gross_white", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_arctic_white", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_sage_green", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_charcoal_grey", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "arctic_blue", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "terracotta", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "cashmere", variantName: ""},
            ],
        },
    })

    let BasinFinishCountertop = new ConfigurationGroupNode({
        parameters: {
            groupId: "countertop_basin_finish",
            groupName: "",
            currentVariant: {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_gross_white", variantName: ""},
            allVariants: [
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_gross_white", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_arctic_white", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_sage_green", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_desert_grey", variantName: ""},
            ],
        },
    })

    let BasinOverflow = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow",
            groupName: "",
            currentVariant: {groupId: "overflow", groupName: "", variantId: "with_overflow", variantName: ""},
            allVariants: [
                {groupId: "overflow", groupName: "", variantId: "with_overflow", variantName: ""},
                {groupId: "overflow", groupName: "", variantId: "without_oferflow", variantName: ""},
            ],
        },
    })

    //---------------------------------------------Maps for config group assignment---------------------------------------------

    const vanityColorWithNaturalOakConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Arctic White", [VanityColourWithNaturalOak, "arctic_white"]],
        ["Sage Green", [VanityColourWithNaturalOak, "sage_green"]],
        ["Charcoal Grey", [VanityColourWithNaturalOak, "charcoal_grey"]],
        ["Atlantic Blue", [VanityColourWithNaturalOak, "atlantic_blue"]],
        ["Terracotta", [VanityColourWithNaturalOak, "terracotta"]],
        ["Cashmere", [VanityColourWithNaturalOak, "cashmere"]],
        ["Natural Oak", [VanityColourWithNaturalOak, "natural_oak"]],
    ])

    const vanityColorConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Arctic White", [VanityColourWithNaturalOak, "arctic_white"]],
        ["Sage Green", [VanityColourWithNaturalOak, "sage_green"]],
        ["Charcoal Grey", [VanityColourWithNaturalOak, "charcoal_grey"]],
        ["Atlantic Blue", [VanityColourWithNaturalOak, "atlantic_blue"]],
        ["Terracotta", [VanityColourWithNaturalOak, "terracotta"]],
        ["Cashmere", [VanityColourWithNaturalOak, "cashmere"]],
    ])

    const handleFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Chrome", [HandleFinish, "handle_comb_chrome"]],
        ["Matt Black", [HandleFinish, "handle_comb_matt_black"]],
        ["Satin Brass", [HandleFinish, "handle_comb_satin_brass"]],
        ["Pewter", [HandleFinish, "handle_comb_pewter"]],
    ])

    const bottleTrapFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Chrome", [BottleTrapFinish, "bottle_trap_chrome"]],
        ["Matt Black", [BottleTrapFinish, "bottle_trap_matt_black"]],
        ["Satin Brass", [BottleTrapFinish, "bottle_trap_satin_brass"]],
        ["Pewter", [BottleTrapFinish, "bottle_trap_pewter"]],
    ])

    const tapFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Chrome", [TapFinish, "tap_comb_chrome"]],
        ["Matt Black", [TapFinish, "tap_comb_matt_black"]],
        ["Satin Brass", [TapFinish, "tap_comb_satin_brass"]],
        ["Pewter", [TapFinish, "tap_comb_pewter"]],
    ])

    const overflowCoverFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Chrome", [BasinOverflowFinish, "overflow_chrome"]],
        ["Matt Black", [BasinOverflowFinish, "overflow_matt_black"]],
        ["Satin Brass", [BasinOverflowFinish, "overflow_satin_brass"]],
        ["Pewter", [BasinOverflowFinish, "overflow_pewter"]],
    ])

    const basinFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Arctic White", [BasinFinish, "mineral_arctic_white"]],
        ["Gloss White", [BasinFinish, "mineral_gross_white"]],
    ])

    const basinFinishCountertopConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Arctic White", [BasinFinishCountertop, "ct_basin_arctic_white"]],
        ["Gloss White", [BasinFinishCountertop, "ct_basin_gross_white"]],
        ["Sage Green", [BasinFinishCountertop, "ct_basin_sage_green"]],
        ["Cashmere", [BasinFinishCountertop, "ct_basin_cashmere"]],
    ])

    const worktopFinishConfigMap = new Map<string, [ConfigurationGroupNode, string]>([
        ["Gloss White", [WorktopFinish, "wt_gross_white"]],
        ["Arctic White", [WorktopFinish, "wt_arctic_white"]],
        ["Sage Green", [WorktopFinish, "wt_sage_green"]],
        ["Charcoal Grey", [WorktopFinish, "wt_charcoal_grey"]],
        ["Atlantic Blue", [WorktopFinish, "atlantic_blue"]],
        ["Terracotta", [WorktopFinish, "terracotta"]],
        ["Cashmere", [WorktopFinish, "cashmere"]],
    ])

    //---------------------------------------------Handle amount---------------------------------------------

    /*Remark: The variant condition node allows only for a single boolean operation between the variant ids.
    Therefore, (a || b || c) && d needs to be expressed as (a && d), (b && d), (c && d).
    Might be worth adding a generic boolean operation node if things get more complex.*/
    const handleAmount2_500 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 2/500",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["500", "floor_mounted"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmount2_600 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 2/600",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["600", "floor_mounted"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })
    const handleAmount2_800 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 2/800",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["800", "floor_mounted"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmount2_1000 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 2/1000",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["1000", "wall_hung"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmount2_1200 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 2/1200",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["1200", "wall_hung"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmount4_1000 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 4/1000",
            amount: 4,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["1000", "floor_mounted"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmount4_1200 = new ConditionalAmountNode({
        parameters: {
            description: "Handle: 4/1200",
            amount: 4,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["1200", "floor_mounted"], variantOperator: "AND", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize, VanityMount]}}),
                },
            }),
        },
    })

    const handleAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Handle amount",
            amountNodes: new ConditionalAmountList({
                parameters: {
                    list: [
                        handleAmount2_500,
                        handleAmount2_600,
                        handleAmount2_800,
                        handleAmount2_1000,
                        handleAmount2_1200,
                        handleAmount4_1000,
                        handleAmount4_1200,
                    ],
                },
            }),
        },
    })
    result.amountGroups.push(handleAmountGroup)

    //---------------------------------------------Mixer & basin amount---------------------------------------------

    const mixerAmount2 = new ConditionalAmountNode({
        parameters: {
            description: "Lever: 2",
            amount: 2,
            condition: new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["1200", "1000"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
                },
            }),
        },
    })

    const mixerAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Mixer amount",
            amountNodes: new ConditionalAmountList({parameters: {list: [mixerAmount2]}}),
        },
    })
    result.amountGroups.push(mixerAmountGroup)

    //---------------------------------------------Bar/knob handle (shared across all units with different amounts)-------------
    const createHandles = (): PriceContainer => {
        //containers are added further below to keep the output order of the api consistent
        //TODO: there is a small furniture knob single, which was not added to the configurator yet.
        const handleKnobUnits = createVariantConditionedPriceNodes(catalog, "Furniture Knob Single", 4, handleFinishConfigMap, handleAmountGroup, [
            "Fluted",
            "Small",
        ])
        const cHandleKnob = createPriceContainer("Handle knob", "Handle knob", handleKnobUnits)
        cHandleKnob.addDependency(HandleType, "knob", "")

        const handleKnobFlutedUnits = createVariantConditionedPriceNodes(catalog, "Fluted Furniture Knob Single", 4, handleFinishConfigMap, handleAmountGroup)
        const cHandleKnobFluted = createPriceContainer("Handle knob fluted", "Handle knob fluted", handleKnobFlutedUnits)
        cHandleKnobFluted.addDependency(HandleType, "knob_fluted", "")

        const handleKnobSmallUnits = createVariantConditionedPriceNodes(catalog, "Small Furniture Knob Single", 4, handleFinishConfigMap, handleAmountGroup)
        const cHandleKnobSmall = createPriceContainer("Handle knob small", "Handle knob small", handleKnobSmallUnits)
        cHandleKnobSmall.addDependency(HandleType, "knob_small", "")

        const handleBarUnits = createVariantConditionedPriceNodes(catalog, "Furniture Bar Handle Single", 4, handleFinishConfigMap, handleAmountGroup)
        const cHandleBar = createPriceContainer("Handle bar", "Handle bar", handleBarUnits)
        cHandleBar.addDependency(HandleType, "bar", "")

        const handleTopMountUnits = createVariantConditionedPriceNodes(catalog, "Furniture Top Mount Handle", 4, handleFinishConfigMap, handleAmountGroup)
        const cHandleTopMount = createPriceContainer("Handle top mount", "Handle top mount", handleTopMountUnits)
        cHandleTopMount.addDependency(HandleType, "top_mount", "")

        //TODO: this was part of an old catalog, but got dropped? The handle that is inside the configurator is fluted
        // const handlePullUnits = createVariantConditionedPriceNodes(catalog, "Furniture Pull Handle", 4, handleFinishConfigMap, handleAmountGroup, ["Fluted"])
        // const cHandlePull = createPriceContainer("Handle pull", "Handle pull", handlePullUnits)
        // cHandlePull.addDependency(HandleType, "pull_handle", "")

        const handleFlutedPullUnits = createVariantConditionedPriceNodes(catalog, "Fluted Furniture Pull Handle", 4, handleFinishConfigMap, handleAmountGroup)
        const cHandleFlutedPull = createPriceContainer("Handle fluted pull", "Handle fluted pull", handleFlutedPullUnits)
        cHandleFlutedPull.addDependency(HandleType, "pull_handle", "")

        //only for grouping -> container could use a dependsOnSubprice, just as the priced item node.
        const cHandles = createPriceContainer(
            "Handles",
            "Handles",
            [cHandleKnob, cHandleKnobFluted, cHandleKnobSmall, cHandleBar, cHandleTopMount, cHandleFlutedPull],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["400_left", "400_right", "500", "600", "800", "1000", "1200"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
                },
            }),
        )

        return cHandles
    }

    //---------------------------------------------400mm---------------------------------------------
    const create400Container = (): PriceContainer => {
        const units400FloorSmoothLeft = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Smooth Cloakroom Unit - floor mounted Left Pull",
            6,
            vanityColorConfigMap,
        )
        const c400FloorSmoothLeft = createPriceContainer("Floor/Smooth", "Floor/Smooth", units400FloorSmoothLeft)
        c400FloorSmoothLeft.addDependency(VanityMount, "floor_mounted", "")
        c400FloorSmoothLeft.addDependency(VanityStyle, "smooth", "")
        c400FloorSmoothLeft.addDependency(VanitySize, "400_left", "")

        const units400FloorSmoothRight = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Smooth Cloakroom Unit - floor mounted Right Pull",
            6,
            vanityColorConfigMap,
        )
        const c400FloorSmoothRight = createPriceContainer("Floor/Smooth", "Floor/Smooth", units400FloorSmoothRight)
        c400FloorSmoothRight.addDependency(VanityMount, "floor_mounted", "")
        c400FloorSmoothRight.addDependency(VanityStyle, "smooth", "")
        c400FloorSmoothRight.addDependency(VanitySize, "400_right", "")

        const units400WallSmoothLeft = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Smooth Cloakroom Unit - wall hung Left Pull",
            6,
            vanityColorConfigMap,
        )
        const c400WallSmoothLeft = createPriceContainer("Wall/Smooth", "Wall/Smooth", units400WallSmoothLeft)
        c400WallSmoothLeft.addDependency(VanityMount, "wall_hung", "")
        c400WallSmoothLeft.addDependency(VanityStyle, "smooth", "")
        c400WallSmoothLeft.addDependency(VanitySize, "400_left", "")

        const units400WallSmoothRight = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Smooth Cloakroom Unit - wall hung Right Pull",
            6,
            vanityColorConfigMap,
        )
        const c400WallSmoothRight = createPriceContainer("Wall/Smooth", "Wall/Smooth", units400WallSmoothRight)
        c400WallSmoothRight.addDependency(VanityMount, "wall_hung", "")
        c400WallSmoothRight.addDependency(VanityStyle, "smooth", "")
        c400WallSmoothRight.addDependency(VanitySize, "400_right", "")

        const units400FloorFlutedLeft = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Fluted Cloakroom unit - floor mounted Left Pull",
            7,
            vanityColorWithNaturalOakConfigMap,
        )
        const c400FloorFlutedLeft = createPriceContainer("Floor/Fluted/Left", "Floor/Fluted/Left", units400FloorFlutedLeft)
        c400FloorFlutedLeft.addDependency(VanityMount, "floor_mounted", "")
        c400FloorFlutedLeft.addDependency(VanityStyle, "fluted", "")
        c400FloorFlutedLeft.addDependency(VanitySize, "400_left", "")

        const units400FloorFlutedRight = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Fluted Cloakroom unit - floor mounted Right Pull",
            7,
            vanityColorWithNaturalOakConfigMap,
        )
        const c400FloorFlutedRight = createPriceContainer("Floor/Fluted/Right", "Floor/Fluted/Right", units400FloorFlutedRight)
        c400FloorFlutedRight.addDependency(VanityMount, "floor_mounted", "")
        c400FloorFlutedRight.addDependency(VanityStyle, "fluted", "")
        c400FloorFlutedRight.addDependency(VanitySize, "400_right", "")

        const units400WallFlutedLeft = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Fluted Cloakroom unit - wall hung Left Pull",
            7,
            vanityColorWithNaturalOakConfigMap,
        )
        const c400WallFlutedLeft = createPriceContainer("Wall/Fluted/Left", "Wall/Fluted/Left", units400WallFlutedLeft)
        c400WallFlutedLeft.addDependency(VanityMount, "wall_hung", "")
        c400WallFlutedLeft.addDependency(VanityStyle, "fluted", "")
        c400WallFlutedLeft.addDependency(VanitySize, "400_left", "")

        const units400WallFlutedRight = createVariantConditionedPriceNodes(
            catalog,
            "Safari 400mm Fluted Cloakroom unit - wall hung Right Pull",
            7,
            vanityColorWithNaturalOakConfigMap,
        )
        const c400WallFlutedRight = createPriceContainer("Wall/Fluted/Right", "Wall/Fluted/Right", units400WallFlutedRight)
        c400WallFlutedRight.addDependency(VanityMount, "wall_hung", "")
        c400WallFlutedRight.addDependency(VanityStyle, "fluted", "")
        c400WallFlutedRight.addDependency(VanitySize, "400_right", "")

        const create400Basins = (): PricedItemNode[] => {
            const unitsBasin400Ceramic = createVariantConditionedPriceNodes(catalog, "400mm Ceramic Furniture Basin", 1)
            unitsBasin400Ceramic[0].addDependency(BasinDesign400_500, "ceramic_400-500", "")
            const unitsBasin400Mineral = createVariantConditionedPriceNodes(catalog, "400mm Mineral Cast Furniture Basin", 1)
            unitsBasin400Mineral[0].addDependency(BasinDesign400_500, "mineral_400-500", "")
            return [...unitsBasin400Ceramic, ...unitsBasin400Mineral]
        }

        return createPriceContainer(
            "400mm",
            "400mm",
            [
                c400FloorSmoothLeft,
                c400FloorSmoothRight,
                c400WallSmoothLeft,
                c400WallSmoothRight,
                c400FloorFlutedLeft,
                c400FloorFlutedRight,
                c400WallFlutedLeft,
                c400WallFlutedRight,
                ...create400Basins(),
            ],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["400_left", "400_right"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
                },
            }),
        )
    }

    //---------------------------------------------500mm---------------------------------------------
    const create500Furniture = () => {
        const units500FloorSmooth = createVariantConditionedPriceNodes(
            catalog,
            "Safari 500mm Smooth Vanity Unit - floor mounted",
            6,
            vanityColorConfigMap,
            mixerAmountGroup,
        )
        const units500WallSmooth = createVariantConditionedPriceNodes(
            catalog,
            "Safari 500mm Smooth Vanity Unit - wall hung",
            6,
            vanityColorConfigMap,
            mixerAmountGroup,
        )
        const units500FloorFluted = createVariantConditionedPriceNodes(
            catalog,
            "Safari 500mm Fluted Vanity Unit - floor mounted",
            7,
            vanityColorWithNaturalOakConfigMap,
            mixerAmountGroup,
        )
        const units500WallFluted = createVariantConditionedPriceNodes(
            catalog,
            "Safari 500mm Fluted Vanity Unit - wall hung",
            7,
            vanityColorWithNaturalOakConfigMap,
            mixerAmountGroup,
        )

        const c500FloorSmooth = createPriceContainer("Smooth", "Smooth", units500FloorSmooth)
        c500FloorSmooth.addDependency(VanityStyle, "smooth", "")
        const c500FloorFluted = createPriceContainer("Fluted", "Fluted", units500FloorFluted)
        c500FloorFluted.addDependency(VanityStyle, "fluted", "")
        const c500Floor = createPriceContainer("Floor", "Floor", [c500FloorSmooth, c500FloorFluted])
        c500Floor.addDependency(VanityMount, "floor_mounted", "")

        const c500WallSmooth = createPriceContainer("Smooth", "Smooth", units500WallSmooth)
        c500WallSmooth.addDependency(VanityStyle, "smooth", "")
        const c500WallFluted = createPriceContainer("Fluted", "Fluted", units500WallFluted)
        c500WallFluted.addDependency(VanityStyle, "fluted", "")
        const c500Wall = createPriceContainer("Wall", "Wall", [c500WallSmooth, c500WallFluted])
        c500Wall.addDependency(VanityMount, "wall_hung", "")
        return [c500Floor, c500Wall]
    }

    //also used for the 1000 version (twice)
    const furniture500 = create500Furniture()

    const create500Container = (): PriceContainer => {
        const create500Basins = (): PricedItemNode[] => {
            const unitsBasin500Ceramic = createVariantConditionedPriceNodes(catalog, "500mm Ceramic Furniture Basin", 1)
            unitsBasin500Ceramic[0].addDependency(BasinDesign400_500, "ceramic_400-500", "")
            const unitsBasin500Mineral = createVariantConditionedPriceNodes(catalog, "500mm Mineral Cast Furniture Basin", 1)
            unitsBasin500Mineral[0].addDependency(BasinDesign400_500, "mineral_400-500", "")
            return [...unitsBasin500Ceramic, ...unitsBasin500Mineral]
        }

        const c500 = createPriceContainer("500mm", "500mm", [...furniture500, ...create500Basins()])
        c500.addDependency(VanitySize, "500", "")
        return c500
    }

    //---------------------------------------------600mm---------------------------------------------
    const create600Furniture = () => {
        const units600FloorSmooth = createVariantConditionedPriceNodes(
            catalog,
            "Safari 600mm Smooth Vanity Unit - floor mounted",
            6,
            vanityColorConfigMap,
            mixerAmountGroup,
        )
        const units600WallSmooth = createVariantConditionedPriceNodes(
            catalog,
            "Safari 600mm Smooth Vanity Unit - wall hung",
            6,
            vanityColorConfigMap,
            mixerAmountGroup,
        )
        const units600FloorFluted = createVariantConditionedPriceNodes(
            catalog,
            "Safari 600mm Fluted Vanity Unit - floor mounted",
            7,
            vanityColorWithNaturalOakConfigMap,
            mixerAmountGroup,
        )
        const units600WallFluted = createVariantConditionedPriceNodes(
            catalog,
            "Safari 600mm Fluted Vanity Unit - wall hung",
            7,
            vanityColorWithNaturalOakConfigMap,
            mixerAmountGroup,
        )

        const c600FloorSmooth = createPriceContainer("Smooth", "Smooth", units600FloorSmooth)
        c600FloorSmooth.addDependency(VanityStyle, "smooth", "")
        const c600FloorFluted = createPriceContainer("Fluted", "Fluted", units600FloorFluted)
        c600FloorFluted.addDependency(VanityStyle, "fluted", "")
        const c600Floor = createPriceContainer("Floor", "Floor", [c600FloorSmooth, c600FloorFluted])
        c600Floor.addDependency(VanityMount, "floor_mounted", "")

        const c600WallSmooth = createPriceContainer("Smooth", "Smooth", units600WallSmooth)
        c600WallSmooth.addDependency(VanityStyle, "smooth", "")
        const c600WallFluted = createPriceContainer("Fluted", "Fluted", units600WallFluted)
        c600WallFluted.addDependency(VanityStyle, "fluted", "")
        const c600Wall = createPriceContainer("Wall", "Wall", [c600WallSmooth, c600WallFluted])
        c600Wall.addDependency(VanityMount, "wall_hung", "")
        return [c600Floor, c600Wall]
    }

    //also used for the 1200 version (twice)
    const furniture600 = create600Furniture()

    const create600Container = (): PriceContainer => {
        const create600Basins = (): PricedItemNode[] => {
            const unitsBasin600Ceramic = createVariantConditionedPriceNodes(catalog, "600mm Ceramic Furniture Basin", 1)
            unitsBasin600Ceramic[0].addDependency(BasinDesign600_800, "ceramic_600-800", "")
            const unitsBasin600Mineral = createVariantConditionedPriceNodes(catalog, "600mm Mineral Cast Furniture Basin", 1)
            unitsBasin600Mineral[0].addDependency(BasinDesign600_800, "mineral_600-800", "")
            return [...unitsBasin600Ceramic, ...unitsBasin600Mineral]
        }

        const createWorktop = (): PriceContainer => {
            const unitsWorktop = createVariantConditionedPriceNodes(catalog, "600mm Worktop", 7, worktopFinishConfigMap)
            const cWorktop = createPriceContainer("Worktop", "Worktop", unitsWorktop)
            cWorktop.addDependency(BasinDesign600_800, "countertop_600-800", "")
            return cWorktop
        }

        const c600 = createPriceContainer("600mm", "600mm", [...furniture600, ...create600Basins(), createWorktop()])
        c600.addDependency(VanitySize, "600", "")
        return c600
    }

    //---------------------------------------------800mm---------------------------------------------

    const create800Container = (): PriceContainer => {
        const create800Furniture = () => {
            const units800FloorSmooth = createVariantConditionedPriceNodes(
                catalog,
                "Safari 800mm Smooth Vanity Unit - floor mounted",
                6,
                vanityColorWithNaturalOakConfigMap,
            )
            const units800WallSmooth = createVariantConditionedPriceNodes(
                catalog,
                "Safari 800mm Smooth Vanity Unit - wall hung",
                6,
                vanityColorWithNaturalOakConfigMap,
            )
            const units800FloorFluted = createVariantConditionedPriceNodes(
                catalog,
                "Safari 800mm Fluted Vanity Unit - floor mounted",
                7,
                vanityColorWithNaturalOakConfigMap,
            )
            const units800WallFluted = createVariantConditionedPriceNodes(
                catalog,
                "Safari 800mm Fluted Vanity Unit - wall hung",
                7,
                vanityColorWithNaturalOakConfigMap,
            )

            const c800FloorSmooth = createPriceContainer("Smooth", "Smooth", units800FloorSmooth)
            c800FloorSmooth.addDependency(VanityStyle, "smooth", "")
            const c800FloorFluted = createPriceContainer("Fluted", "Fluted", units800FloorFluted)
            c800FloorFluted.addDependency(VanityStyle, "fluted", "")
            const c800Floor = createPriceContainer("Floor", "Floor", [c800FloorSmooth, c800FloorFluted])
            c800Floor.addDependency(VanityMount, "floor_mounted", "")

            const c800WallSmooth = createPriceContainer("Smooth", "Smooth", units800WallSmooth)
            c800WallSmooth.addDependency(VanityStyle, "smooth", "")
            const c800WallFluted = createPriceContainer("Fluted", "Fluted", units800WallFluted)
            c800WallFluted.addDependency(VanityStyle, "fluted", "")
            const c800Wall = createPriceContainer("Wall", "Wall", [c800WallSmooth, c800WallFluted])
            c800Wall.addDependency(VanityMount, "wall_hung", "")
            return [c800Floor, c800Wall]
        }
        const create800Basins = (): PricedItemNode[] => {
            const unitsBasin800Ceramic = createVariantConditionedPriceNodes(catalog, "800mm Ceramic Furniture Basin", 1)
            unitsBasin800Ceramic[0].addDependency(BasinDesign600_800, "ceramic_600-800", "")
            const unitsBasin800Mineral = createVariantConditionedPriceNodes(catalog, "800mm Mineral Cast Furniture Basin", 1)
            unitsBasin800Mineral[0].addDependency(BasinDesign600_800, "mineral_600-800", "")
            return [...unitsBasin800Ceramic, ...unitsBasin800Mineral]
        }

        const createWorktop = (): PriceContainer => {
            const unitsWorktop = createVariantConditionedPriceNodes(catalog, "800mm Worktop", 7, worktopFinishConfigMap)
            const cWorktop = createPriceContainer("Worktop", "Worktop", unitsWorktop)
            cWorktop.addDependency(BasinDesign600_800, "countertop_600-800", "")
            return cWorktop
        }

        const c800 = createPriceContainer("800mm", "800mm", [...create800Furniture(), ...create800Basins(), createWorktop()])
        c800.addDependency(VanitySize, "800", "")
        return c800
    }
    //---------------------------------------------1000/1200mm connector---------------------------------------------

    const createScrews = (): PriceContainer => {
        const link = createVariantConditionedPriceNodes(catalog, "Safari Furniture Joining Screws For use with 500mm and 600mm units", 1)
        const conditionForLink = new VariantConditionNode({
            parameters: {
                condition: {variantIds: ["1000", "1200"], variantOperator: "OR", negated: false},
                currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
            },
        })
        link[0].updateParameters({condition: conditionForLink})

        const c10001200Screws = createPriceContainer("Joining Screws", "Joining Screws", link, conditionForLink)
        return c10001200Screws
        //return link[0]
    }

    //---------------------------------------------1000mm---------------------------------------------

    const create1000Container = (): PriceContainer => {
        const createWorktop = (): PriceContainer => {
            const unitsWorktop = createVariantConditionedPriceNodes(catalog, "1000mm Worktop", 7, worktopFinishConfigMap)
            const cWorktop = createPriceContainer("Worktop", "Worktop", unitsWorktop)
            cWorktop.addDependency(BasinDesign1000_1200, "countertop_1000-1200", "")
            return cWorktop
        }

        const create1000Basins = (): PricedItemNode[] => {
            return createVariantConditionedPriceNodes(catalog, "1000mm Mineral Cast Double Furniture Basin", 2, basinFinishConfigMap)
        }
        const c1000 = createPriceContainer("1000mm", "1000mm", [...furniture500, createWorktop(), ...create1000Basins()])
        c1000.addDependency(VanitySize, "1000", "")
        return c1000
    }

    //---------------------------------------------1200mm---------------------------------------------

    const create1200Container = (): PriceContainer => {
        const createWorktop = (): PriceContainer => {
            const unitsWorktop = createVariantConditionedPriceNodes(catalog, "1200mm Worktop", 7, worktopFinishConfigMap)
            const cWorktop = createPriceContainer("Worktop", "Worktop", unitsWorktop)
            cWorktop.addDependency(BasinDesign1000_1200, "countertop_1000-1200", "")
            return cWorktop
        }

        const create1200Basins = (): PricedItemNode[] => {
            return createVariantConditionedPriceNodes(catalog, "1200mm Mineral Cast Double Furniture Basin", 2, basinFinishConfigMap)
        }
        const c1200 = createPriceContainer("1200mm", "1200mm", [...furniture600, createWorktop(), ...create1200Basins()])
        c1200.addDependency(VanitySize, "1200", "")
        return c1200
    }

    //---------------------------------------------400 mini mixers---------------------------------------------

    const createMiniMixers = () => {
        //Safari Lever Cold Start Mini Mono Basin Mixer
        const unitsMiniMixerLever = createVariantConditionedPriceNodes(
            catalog,
            "Safari Lever Cold Start Mini Mono Basin Mixer",
            4,
            tapFinishConfigMap,
            undefined,
            ["Basin Waste"],
        )

        const unitsMiniMixerLeverless = createVariantConditionedPriceNodes(
            catalog,
            "Safari Leverless Cold Start Mini Mono Basin Mixer",
            4,
            tapFinishConfigMap,
            undefined,
            ["Basin Waste"],
        )

        const unitsMiniMixerLeverBasinWaste = createVariantConditionedPriceNodes(
            catalog,
            "Safari Lever Cold Start Mini Mono Basin Mixer + Universal Basin Waste",
            4,
            tapFinishConfigMap,
        )

        const unitsMiniMixerLeverlessBasinWaste = createVariantConditionedPriceNodes(
            catalog,
            "Safari Leverless Cold Start Mini Mono Basin Mixer + Universal Basin Waste",
            4,
            tapFinishConfigMap,
        )

        const cMiniMixerLeverNoWaste = createPriceContainer("No Waste", "No Waste", unitsMiniMixerLever)
        cMiniMixerLeverNoWaste.addDependency(BasinWaste, "comb_without_waste", "")
        const cMiniMixerLeverWithWaste = createPriceContainer("With Waste", "With Waste", unitsMiniMixerLeverBasinWaste)
        cMiniMixerLeverWithWaste.addDependency(BasinWaste, "comb_with_waste", "")
        const cMiniMixerLever = createPriceContainer("Lever", "Lever", [cMiniMixerLeverNoWaste, cMiniMixerLeverWithWaste])
        cMiniMixerLever.addDependency(LeverOption, "lever_handle", "")

        const cMiniMixerLeverlessNoWaste = createPriceContainer("No Waste", "No Waste", unitsMiniMixerLeverless)
        cMiniMixerLeverlessNoWaste.addDependency(BasinWaste, "comb_without_waste", "")
        const cMiniMixerLeverlessWithWaste = createPriceContainer("With Waste", "With Waste", unitsMiniMixerLeverlessBasinWaste)
        cMiniMixerLeverlessWithWaste.addDependency(BasinWaste, "comb_with_waste", "")
        const cMiniMixerLeverless = createPriceContainer("Leverless", "Leverless", [cMiniMixerLeverlessNoWaste, cMiniMixerLeverlessWithWaste])
        cMiniMixerLeverless.addDependency(LeverOption, "leverless_handle", "")

        const cMiniMixer = createPriceContainer(
            "Mini Mixer",
            "Mini Mixer",
            [cMiniMixerLever, cMiniMixerLeverless],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["400_left", "400_right"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
                },
            }),
        )

        return cMiniMixer
    }

    //---------------------------------------------Basin waste---------------------------------------------

    const createUniversalBasinWaste = (): PriceContainer => {
        const unitsUniversalBasinWaste = createVariantConditionedPriceNodes(catalog, "Universal Basin Waste", 4, tapFinishConfigMap, mixerAmountGroup, [
            "Mixer",
        ])
        const cUniversalBasinWaste = createPriceContainer("Universal Basin Waste", "Universal Basin Waste", unitsUniversalBasinWaste)
        cUniversalBasinWaste.addDependency(BasinWaste, "comb_with_waste", "")

        const cRelevantConfigurations = createPriceContainer(
            "Relevant Configurations Basin Waste",
            "Relevant Configurations Basin Waste",
            [cUniversalBasinWaste],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["400_left", "400_right", "500", "100/200", "ceramic_600-800"], variantOperator: "OR", negated: true},
                    currentConfigurations: new ConfigurationList({parameters: {list: [TapMineralInset, BasinDesign600_800, VanitySize]}}),
                },
            }),
        )

        return cRelevantConfigurations
    }

    const cUniversalBasinWaste = createUniversalBasinWaste()

    //---------------------------------------------500/600/800/1000/1200mm mixers---------------------------------------------
    const createDeckMountedMono = (): PriceContainer => {
        const unitsMixerLever = createVariantConditionedPriceNodes(
            catalog,
            "Safari Lever Cold Start Mono Basin Mixer",
            4,
            tapFinishConfigMap,
            mixerAmountGroup,
            ["Basin Waste", "Pressure"],
        )
        const unitsMixerLeverless = createVariantConditionedPriceNodes(
            catalog,
            "Safari Leverless Cold Start Mono Basin Mixer",
            4,
            tapFinishConfigMap,
            mixerAmountGroup,
            ["Basin Waste", "Pressure"],
        )
        const unitsMixerLeverWithWaste = createVariantConditionedPriceNodes(
            catalog,
            "Safari Lever Cold Start Mono Basin Mixer + Universal Basin Waste",
            4,
            tapFinishConfigMap,
            mixerAmountGroup,
            ["Pressure"],
        )
        const unitsMixerLeverlessWithWaste = createVariantConditionedPriceNodes(
            catalog,
            "Leverless Cold Start Mono Basin Mixer + Universal Basin Waste",
            4,
            tapFinishConfigMap,
            mixerAmountGroup,
            ["Pressure"],
        )

        const cMixerLeverNoWaste = createPriceContainer("No Waste", "No Waste", unitsMixerLever)
        cMixerLeverNoWaste.addDependency(BasinWaste, "comb_without_waste", "")
        const cMixerLeverWithWaste = createPriceContainer("With Waste", "With Waste", unitsMixerLeverWithWaste)
        cMixerLeverWithWaste.addDependency(BasinWaste, "comb_with_waste", "")
        const cMixerLever = createPriceContainer("Lever", "Lever", [cMixerLeverNoWaste, cMixerLeverWithWaste])
        cMixerLever.addDependency(LeverOption, "lever_handle", "")

        const cMixerLeverlessNoWaste = createPriceContainer("No Waste", "No Waste", unitsMixerLeverless)
        cMixerLeverlessNoWaste.addDependency(BasinWaste, "comb_without_waste", "")
        const cMixerLeverlessWithWaste = createPriceContainer("With Waste", "With Waste", unitsMixerLeverlessWithWaste)
        cMixerLeverlessWithWaste.addDependency(BasinWaste, "comb_with_waste", "")
        const cMixerLeverless = createPriceContainer("Leverless", "Leverless", [cMixerLeverlessNoWaste, cMixerLeverlessWithWaste])
        cMixerLeverless.addDependency(LeverOption, "leverless_handle", "")

        const cDeckMountedMono = createPriceContainer(
            "Deck Mounted Mono",
            "Deck Mounted Mono",
            [cMixerLever, cMixerLeverless],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["100/200", "ceramic_600-800", "500"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [TapMineralInset, BasinDesign600_800, VanitySize]}}),
                },
            }),
        )

        return cDeckMountedMono
    }

    const deckMountedMono = createDeckMountedMono()

    const create500Mixers = () => {
        const c500 = createPriceContainer("Mixers 500mm", "Mixers 500mm", [deckMountedMono])
        c500.addDependency(VanitySize, "500", "")
        return c500
    }

    const create600_800_1000_1200Mixers = () => {
        const createThreeHole = (): PriceContainer[] => {
            const unitsThreeHoleDeckMountedLever = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Three Hole Deck Mounted Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Spout", "Pressure"],
            )

            const unitsThreeHoleDeckMountedLeverless = createVariantConditionedPriceNodes(
                catalog,
                "Safari Leverless Three Hole Deck Mounted Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Spout", "Pressure"],
            )

            const unitsThreeHoleDeckMountedSpoutLever = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Three Hole Deck Mounted Basin Mixer with Pull Out Spout",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
            )

            const unitsThreeHoleDeckMountedSpoutLeverless = createVariantConditionedPriceNodes(
                catalog,
                "Safari Leverless Three Hole Deck Mounted Basin Mixer with Pull Out Spout",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
            )

            const cThreeHoleDeckMountedLever = createPriceContainer("Lever", "Lever", unitsThreeHoleDeckMountedLever)
            cThreeHoleDeckMountedLever.addDependency(LeverOption, "lever_handle", "")
            const cThreeHoleDeckMountedLeverless = createPriceContainer("Leverless", "Leverless", unitsThreeHoleDeckMountedLeverless)
            cThreeHoleDeckMountedLeverless.addDependency(LeverOption, "leverless_handle", "")

            const cThreeHoleDeckMounted = createPriceContainer("Three Hole Deck Mounted", "Three Hole Deck Mounted", [
                cThreeHoleDeckMountedLever,
                cThreeHoleDeckMountedLeverless,
            ])
            cThreeHoleDeckMounted.addDependency(TapMineralInset, "101/201", "")

            const cThreeHoleDeckMountedSpoutLever = createPriceContainer("Lever", "Lever", unitsThreeHoleDeckMountedSpoutLever)
            cThreeHoleDeckMountedSpoutLever.addDependency(LeverOption, "lever_handle", "")
            const cThreeHoleDeckMountedSpoutLeverless = createPriceContainer("Leverless", "Leverless", unitsThreeHoleDeckMountedSpoutLeverless)
            cThreeHoleDeckMountedSpoutLeverless.addDependency(LeverOption, "leverless_handle", "")
            const cThreeHoleDeckMountedSpout = createPriceContainer("Three Hole Deck Mounted Spout", "Three Hole Deck Mounted Spout", [
                cThreeHoleDeckMountedSpoutLever,
                cThreeHoleDeckMountedSpoutLeverless,
            ])
            cThreeHoleDeckMountedSpout.addDependency(TapMineralInset, "102/202", "")

            return [cThreeHoleDeckMounted, cThreeHoleDeckMountedSpout]
        }

        const cMixers = createPriceContainer(
            "Mixers",
            "Mixers",
            [deckMountedMono, ...createThreeHole()],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["500", "600", "800", "1000", "1200"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [VanitySize]}}),
                },
            }),
        )

        return cMixers
    }

    //---------------------------------------------600/800/1000/1200mm countertop mixers---------------------------------------------

    const createCountertopMixers = (): PriceContainer => {
        const createExtended = (): PriceContainer => {
            const unitsExtendedLever = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Cold Start Extended Mono Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Pressure"],
            )

            const unitsExtendedLeverless = createVariantConditionedPriceNodes(
                catalog,
                "Safari Leverless Cold Start Extended Mono Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Pressure"],
            )

            const cExtendedLever = createPriceContainer("Lever", "Lever", unitsExtendedLever)
            cExtendedLever.addDependency(LeverOption, "lever_handle", "")
            const cExtendedLeverless = createPriceContainer("Leverless", "Leverless", unitsExtendedLeverless)
            cExtendedLeverless.addDependency(LeverOption, "leverless_handle", "")

            const cExtendedMixers = createPriceContainer("Extended Mixers", "Extended Mixers", [cExtendedLever, cExtendedLeverless])
            cExtendedMixers.addDependency(TapCounter, "100e/200e", "")
            return cExtendedMixers
        }

        const createWallMounted3Hole = (): PriceContainer => {
            const unitsWallMounted3HoleLever = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Three Hole Wall Mounted Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Pressure"],
            )

            const unitsWallMounted3HoleLeverless = createVariantConditionedPriceNodes(
                catalog,
                "Safari Leverless Three Hole Wall Mounted Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Pressure"],
            )

            const cWallMounted3HoleLever = createPriceContainer("Lever", "Lever", unitsWallMounted3HoleLever)
            cWallMounted3HoleLever.addDependency(LeverOption, "lever_handle", "")
            const cWallMounted3HoleLeverless = createPriceContainer("Leverless", "Leverless", unitsWallMounted3HoleLeverless)
            cWallMounted3HoleLeverless.addDependency(LeverOption, "leverless_handle", "")

            const cWallMounted3Hole = createPriceContainer("Wall Mounted 3 Hole", "Wall Mounted 3 Hole", [cWallMounted3HoleLever, cWallMounted3HoleLeverless])
            cWallMounted3Hole.addDependency(TapCounter, "109/209", "")
            return cWallMounted3Hole
        }

        const createWallMounted2Hole = (): PriceContainer => {
            //This one according to the requrements should always have a lever.
            const unitsWallMounted2HoleLever = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Wall Mounted Basin Mixer",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Square", "Pressure"],
            )

            const cWallMounted2Hole = createPriceContainer("Wall Mounted 2 Hole", "Wall Mounted 2 Hole", unitsWallMounted2HoleLever)
            cWallMounted2Hole.addDependency(TapCounter, "109s/209s", "")
            return cWallMounted2Hole
        }

        const createWallMounted2HoleWithBackplate = (): PriceContainer => {
            //This one according to the requrements should always have a lever.
            const wallMounted2HoleWithBackplateLeverUnits = createVariantConditionedPriceNodes(
                catalog,
                "Safari Lever Wall Mounted Basin Mixer with Square Backplate",
                4,
                tapFinishConfigMap,
                mixerAmountGroup,
                ["Pressure"],
            )

            const cWallMounted2HoleWithBackplateLever = createPriceContainer(
                "Wall Mounted 2 Hole, Square Plate",
                "Wall Mounted 2 Hole, Square Plate",
                wallMounted2HoleWithBackplateLeverUnits,
            )
            cWallMounted2HoleWithBackplateLever.addDependency(TapCounter, "109s-sq/209s-sq", "")
            return cWallMounted2HoleWithBackplateLever
        }

        const cCountertopMixers = createPriceContainer(
            "Countertop Mixers",
            "Countertop Mixers",
            [createExtended(), createWallMounted3Hole(), createWallMounted2Hole(), createWallMounted2HoleWithBackplate() /*cUniversalBasinWaste*/],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["countertop_600-800", "countertop_1000-1200"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [BasinDesign600_800, BasinDesign1000_1200]}}),
                },
            }),
        )

        return cCountertopMixers
    }

    //---------------------------------------------600/800/1000/1200mm countertop basins---------------------------------------------

    const createCountertopBasins = (): PriceContainer => {
        const createRoundUnits = (): PriceContainer => {
            const unitsRound = createVariantConditionedPriceNodes(catalog, "Countertop Basin Round", 4, basinFinishCountertopConfigMap, mixerAmountGroup)
            const cRoundUnits = createPriceContainer("Round", "Round", unitsRound)
            cRoundUnits.addDependency(BasinShape, "round", "")

            return cRoundUnits
        }

        const createRoundFlutedUnits = (): PriceContainer => {
            const unitsRoundFluted = createVariantConditionedPriceNodes(
                catalog,
                "Countertop Fluted Basin Round",
                4,
                basinFinishCountertopConfigMap,
                mixerAmountGroup,
            )
            const cRoundFlutedUnits = createPriceContainer("Round Fluted", "Round Fluted", unitsRoundFluted)
            cRoundFlutedUnits.addDependency(BasinShape, "round_fluted", "")

            return cRoundFlutedUnits
        }

        const createSquareUnits = (): PriceContainer => {
            const unitsSquare = createVariantConditionedPriceNodes(catalog, "Countertop Basin Square", 4, basinFinishCountertopConfigMap, mixerAmountGroup)
            const cSquareUnits = createPriceContainer("Square", "Square", unitsSquare)
            cSquareUnits.addDependency(BasinShape, "square", "")

            return cSquareUnits
        }

        return createPriceContainer(
            "Countertop Basins",
            "Countertop Basins",
            [createRoundUnits(), createRoundFlutedUnits(), createSquareUnits()],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["countertop_600-800", "countertop_1000-1200"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [BasinDesign600_800, BasinDesign1000_1200]}}),
                },
            }),
        )
    }

    const createBottleTrap = (): PriceContainer => {
        const unitsBottleTrap = createVariantConditionedPriceNodes(catalog, "Round Bottle Trap", 4, bottleTrapFinishConfigMap, mixerAmountGroup)
        const cBottleTrap = createPriceContainer("Bottle Trap", "Bottle Trap", unitsBottleTrap)
        cBottleTrap.addDependency(BottleTrap, "bottle_trap_with", "")
        return cBottleTrap
    }

    //---------------------------------------------Overflow covers---------------------------------------------

    const createverflowCovers = (): PriceContainer => {
        const unitsCeramicCover = createVariantConditionedPriceNodes(catalog, "Ceramic Basin Overflow Cover", 4, overflowCoverFinishConfigMap, mixerAmountGroup)
        const cCeramicCover = createPriceContainer("Ceramic Cover", "Ceramic Cover", unitsCeramicCover)
        cCeramicCover.addDependency(BasinOverflow, "with_overflow", "")

        const unitsMineralCover = createVariantConditionedPriceNodes(catalog, "Mineral Basin Overflow Cover", 4, overflowCoverFinishConfigMap, mixerAmountGroup)
        const cMineralCover = createPriceContainer(
            "Mineral Cover",
            "Mineral Cover",
            unitsMineralCover,
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["mineral_400-500", "mineral_600-800", "mineral_1000-1200"], variantOperator: "OR", negated: false},
                    currentConfigurations: new ConfigurationList({parameters: {list: [BasinDesign400_500, BasinDesign600_800, BasinDesign1000_1200]}}),
                },
            }),
        )

        const cOverflowCover = createPriceContainer(
            "Overflow Cover",
            "Overflow Cover",
            [cCeramicCover, cMineralCover],
            new VariantConditionNode({
                parameters: {
                    condition: {variantIds: ["countertop_600-800", "countertop_1000-1200"], variantOperator: "OR", negated: true},
                    currentConfigurations: new ConfigurationList({parameters: {list: [BasinDesign600_800, BasinDesign1000_1200]}}),
                },
            }),
        )

        return cOverflowCover
    }

    result.priceContainers.push(
        create400Container(),
        create500Container(),
        create600Container(),
        create800Container(),
        create1000Container(),
        create1200Container(),
        createCountertopBasins(),
        createMiniMixers(),
        create600_800_1000_1200Mixers(),
        createCountertopMixers(),
        cUniversalBasinWaste,
        createHandles(),
        createBottleTrap(),
        createverflowCovers(),
        createScrews(),
    )

    return result
}
